// Cover Design service config (Post a Project, Atrium Stage 7).
//
// Source of truth = the Rails backend, NOT the design prototype:
//   whitelist  app/controllers/api/v1/project_postings_controller.rb (cover_design_posting_params)
//   validation app/models/job_postings/job_postings/step_validations.rb (cover_design__one..six)
//   cost       app/models/job_postings/cover_design_posting.rb (#cost / #add_on_cost)
//   parity     tasks/post-a-project/parity/design-media.md §1
//
// Field keys are the snake_case backend attrs. Composites (consent, sliders) are
// expanded to backend booleans/attrs by serialize.js, not here.

import {
  COVER_PACKAGES,
  NICHE_AND_GENRE_GROUPS,
  INTERIOR_TYPES,
  TRIM_SIZES,
  PAGE_COLORS,
  COVER_FRONT_OPTIONS,
  COVER_BACK_OPTIONS,
  COVER_SPINE_OPTIONS,
  STYLE_SLIDER_AXES,
  RATES,
} from "../options"
import { COVER_SAMPLE_URLS } from "../sampleImages"

// string[] -> [{ value, label }] for select / checkgroup fields.
const asOptions = (arr) => arr.map((v) => ({ value: v, label: v }))

const money = (n) => Math.round(n * 100) / 100

const PLATINUM = "Platinum"

// Design package labels and the "Popular" tag on Gold. Package descriptions come
// from COVER_PACKAGES, which mirrors the backend's revision allowances. The
// submitted VALUE stays the backend package key (Silver/Gold/Platinum).
const COVER_PACKAGE_DISPLAY = {
  Silver: {
    label: "Silver Cover",
  },
  Gold: {
    label: "Gold Cover",
    tag: "Popular",
  },
  Platinum: {
    label: "Platinum Cover",
  },
}

export const coverDesign = {
  key: "cover_design",
  postingKey: "cover_design_posting",
  name: "Cover Design",

  steps: () => [
    // Step 1 — Package (cover_design__one)
    {
      id: "package",
      title: "Cover package",
      sub: "Every package delivers one cover in eBook, audiobook and paperback formats.",
      fields: [
        {
          type: "radio",
          key: "package_type",
          required: true,
          // Card "From" price is server-traced via ctx.rates.cover_design_cost
          // (Insider-aware; ruling C2 / tuw-app:P-001), with the COVER_PACKAGES
          // constant as the no-live-PriceList fallback.
          options: (v = {}, ctx = {}) =>
            COVER_PACKAGES.map((p) => {
              // Per-key: a null server leaf for this package falls back to the
              // COVER_PACKAGES constant starting price (= RATES.COVER_DESIGN_COST[p.value]),
              // never to 0 (tuw-app:P-001).
              const price = ctx.rates?.cover_design_cost?.[p.value] ?? p.startingPrice
              const display = COVER_PACKAGE_DISPLAY[p.value]
              return {
                value: p.value,
                label: display.label,
                sub: p.description,
                tag: display.tag,
                price: `From $${price.toFixed(2)}`,
              }
            }),
        },
      ],
    },

    // Step 2 — Book details (cover_design__two). The 2-option AI consent lives in
    // this step server-side (validated inside cover_design__two), so it renders here.
    // In a bundle, title/subtitle/author_name/niche are inherited from the manuscript
    // (cover_design__two skips them for bundles), so they're hidden here and injected
    // from book_writing at serialize time.
    {
      id: "details",
      title: "Book details",
      sub: "The basics your designer needs.",
      fields: [
        { type: "text", key: "title", label: "Title", required: true, placeholder: "Working title", when: (v, ctx) => !(ctx && ctx.bundle) },
        { type: "text", key: "subtitle", label: "Subtitle", required: false, when: (v, ctx) => !(ctx && ctx.bundle) },
        { type: "text", key: "author_name", label: "Author name", required: true, placeholder: "As it should appear", when: (v, ctx) => !(ctx && ctx.bundle) },
        {
          // Fiction and non-fiction in one grouped list (AU-25). The server
          // whitelist for this field is COVER_NICHES_AND_GENRES, which
          // deliberately excludes the two restricted formatting niches.
          type: "select",
          key: "niche",
          label: "Niche / genre",
          required: true,
          blankLabel: "Select Niche/Genre",
          options: NICHE_AND_GENRE_GROUPS,
          when: (v, ctx) => !(ctx && ctx.bundle),
        },
        {
          type: "select",
          key: "interior_type",
          label: "Interior type",
          required: true,
          blankLabel: "Select interior type",
          options: asOptions(INTERIOR_TYPES),
        },
        {
          type: "select",
          key: "trim_size",
          label: "Trim size",
          required: true,
          blankLabel: "Select Trim Size",
          options: asOptions(TRIM_SIZES),
        },
        {
          type: "select",
          key: "page_color",
          label: "Page color",
          required: true,
          blankLabel: "Select page color",
          options: asOptions(PAGE_COLORS),
        },
        {
          type: "textarea",
          key: "cover_examples",
          label: "Links to covers for inspiration",
          required: false,
          rows: 3,
        },
        {
          // Option VALUES are the backend cover-option constants (constants.rb
          // FRONT/BACK/SPINE_COVER_OPTIONS) that feed the freelancer/order views;
          // the design's typographic apostrophe / spacing variants are display-only,
          // so the options divergence is a registered exception. Field labels match
          // the design.
          type: "checkgroup",
          key: "front_cover_options",
          label: "Front cover should include",
          required: true,
          min: 1,
          options: asOptions(COVER_FRONT_OPTIONS),
        },
        {
          type: "textarea",
          key: "front_cover_details",
          label: "Front cover details",
          required: false,
          rows: 2,
          placeholder: "Provide details.",
        },
        {
          type: "checkgroup",
          key: "back_cover_options",
          label: "Back cover should include",
          required: true,
          min: 1,
          options: asOptions(COVER_BACK_OPTIONS),
        },
        {
          type: "textarea",
          key: "back_cover_details",
          label: "Back cover details",
          required: false,
          rows: 2,
          placeholder: "Provide details.",
        },
        {
          type: "checkgroup",
          key: "spine_cover_options",
          label: "Spine should include",
          required: true,
          min: 1,
          options: asOptions(COVER_SPINE_OPTIONS),
        },
        {
          type: "textarea",
          key: "spine_cover_details",
          label: "Spine details",
          required: false,
          rows: 2,
          placeholder: "Provide details.",
        },
        {
          type: "number",
          key: "number_of_pages",
          label: "Number of pages",
          required: false,
          min: 1,
          max: 5000,
          step: 1,
        },
        {
          type: "textarea",
          key: "back_cover_text",
          label: "Back cover text",
          required: false,
          rows: 4,
          help: "The blurb or copy you want printed on the back cover, if you have it.",
        },
        {
          type: "consent",
          key: "ai_consent",
          mode: "2",
          label: "AI assistance",
          required: true,
          help: "Let us know whether your designer may use AI tools on this cover.",
        },
        {
          type: "file",
          key: "file",
          label: "Requirements file",
          required: false,
          accept: ".doc,.docx,.pdf,.zip,.jpg,.jpeg,.png,.gif",
          placeholder: "Upload a file",
        },
      ],
    },

    // Step 3 — Source files add-on (cover_design_posting add_ons).
    {
      id: "addon",
      title: "Source files",
      sub: "Optional, keep the editable design files.",
      fields: [
        {
          type: "toggle",
          key: "add_ons",
          label: "Add source files (PSD)",
          help: "Get the editable layered source files. Included free with the Platinum package.",
        },
      ],
    },

    // Step 4 — Sample picks (cover_design__four — min 3, max 5).
    {
      id: "samples",
      title: "Pick design samples",
      sub: "Choose 3-5 covers whose style you’re drawn to. Your designer uses these as direction.",
      fields: [
        {
          type: "gallery",
          key: "design_samples",
          required: true,
          min: 3,
          max: 5,
          count: 30,
          noun: "Cover",
          tileShape: "book",
          // Persist backend-legible filenames (cover_sample_0.png..cover_sample_29.png)
          // and render the real sample PNGs (Plumbline bugs 1 + 3).
          valueFor: (i) => `cover_sample_${i}.png`,
          imageFor: (name) => COVER_SAMPLE_URLS[name],
        },
      ],
    },

    // Step 5 — Color palette (cover_design__five — min 1, submit KEYS).
    {
      id: "palette",
      title: "Color direction",
      sub: "Choose at least one palette for your designer to explore.",
      fields: [
        {
          type: "palette",
          key: "color_palettes",
          required: true,
          min: 1,
        },
      ],
    },

    // Step 6 — Style sliders (cover_design__six — at least 2 moved off default).
    {
      id: "style",
      title: "Style sliders",
      sub: "Where should the design sit on each scale? Defaults to the middle.",
      fields: [
        {
          type: "sliders",
          key: "style",
          axes: STYLE_SLIDER_AXES,
          minMoved: 2,
        },
      ],
    },
  ],

  // Instant estimate for the PriceRail. Ruby: cover_design_cost/100 + add_on_cost,
  // where add_on_cost is $0 for Platinum (source files included) or when add_ons off.
  estimate: (values = {}, ctx = {}) => {
    const pkg = values.package_type
    const base = pkg ? (ctx.rates?.cover_design_cost?.[pkg] ?? RATES.COVER_DESIGN_COST[pkg]) : null
    const lines = []

    if (base != null) {
      lines.push({ label: `Cover design (${pkg})`, amount: money(base) })
    }

    if (values.add_ons) {
      if (pkg === PLATINUM) {
        lines.push({ label: "Source files (included with Platinum)", amount: 0, muted: true })
      } else {
        lines.push({
          label: "Source files (PSD)",
          amount: money(ctx.rates?.cover_add_on_cost ?? RATES.COVER_ADD_ON_COST),
        })
      }
    }

    const total = money(lines.reduce((sum, l) => sum + l.amount, 0))

    return { lines, total, isRange: false, framing: "free-to-post" }
  },

  // /post/price entry (camelCase; underscored server-side). cost_fields for
  // cover_design_posting = [package_type, add_ons]. Null until a package is chosen.
  pricePayload: (values = {}) => {
    if (!values.package_type) return null

    return {
      postingType: "cover_design_posting",
      packageType: values.package_type,
      addOns: !!values.add_ons,
    }
  },
}
